import type { TranslationEngine, TranslationLookup, TranslationResources } from '../types';

/**
 * Motor de traducción predeterminado
 * Permite manejar múltiples recursos cargados en memoria y realizar fallbacks
 */
export class DefaultTranslationEngine implements TranslationEngine {
  private resources: { [lang: string]: TranslationResources } = {};
  private currentLanguage: string = 'es';
  private defaultLanguage: string = 'es';

  constructor(defaultLang: string = 'es') {
    this.defaultLanguage = defaultLang;
    this.currentLanguage = defaultLang;
  }

  /**
   * Resuelve una llave indicando en qué idioma se encontró, o `null` si no está
   * en ningún catálogo. Misma búsqueda que `translate()` (idioma actual, luego
   * fallback al default) pero sin colapsar ambos casos en una cadena.
   *
   * @param key Llave del mensaje (ej: 'saludo.bienvenida')
   */
  resolve(key: string): TranslationLookup | null {
    // Se comprueba la EXISTENCIA de la llave (`in`), no que su valor sea truthy.
    // Con un chequeo por truthiness, una traducción legítimamente vacía —una
    // cabecera de columna sin título, por ejemplo— se trataba como inexistente y
    // el motor devolvía la propia llave, que terminaba renderizada en la UI.
    // 1. Intentar en idioma actual
    const currentResources = this.resources[this.currentLanguage];
    if (currentResources && key in currentResources) {
      return { value: currentResources[key], lang: this.currentLanguage };
    }

    // 2. Intentar en idioma default (fallback)
    const defaultResources = this.resources[this.defaultLanguage];
    if (
      this.currentLanguage !== this.defaultLanguage &&
      defaultResources &&
      key in defaultResources
    ) {
      return { value: defaultResources[key], lang: this.defaultLanguage };
    }

    // 3. No existe en ninguna parte
    return null;
  }

  /**
   * Traduce una llave. Busca en el idioma actual y hace fallback al default si falla.
   * @param key Llave del mensaje (ej: 'saludo.bienvenida')
   */
  translate(key: string): string {
    // Devuelve la llave si no existe en ninguna parte, para no romper la UI.
    return this.resolve(key)?.value ?? key;
  }

  /**
   * Registra recursos en el motor
   */
  loadResources(lang: string, resources: TranslationResources): void {
    // Mergeamos si ya existen recursos para ese idioma
    this.resources[lang] = {
      ...(this.resources[lang] || {}),
      ...resources,
    };
  }

  /**
   * Indica si el motor ya tiene recursos en memoria para un idioma.
   * Permite al provider decidir si puede revalidar en segundo plano (ya hay algo
   * que pintar) o si debe esperar la respuesta del backend.
   */
  hasResources(lang: string): boolean {
    const resources = this.resources[lang];
    return Boolean(resources) && Object.keys(resources).length > 0;
  }

  /**
   * Cambia el idioma activo en el motor
   */
  changeLanguage(lang: string): void {
    this.currentLanguage = lang;
  }

  /**
   * Obtiene el código del idioma activo
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }
}
